// components/UI/CountryPromptModal.tsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { updateProfile } from "../../store/slices/userSlice";
import { updateUserCountry } from "../../store/slices/authSlice";
import Modal from "./Modal";
import Button from "./Button";
import globalStyle from "../../styles/globalStyle";
import { COUNTRIES } from "../../utils/countries";
import { MapPinIcon, GlobeAltIcon } from "@heroicons/react/24/outline";

interface CountryPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Note: localStorage key is no longer used for auto-dismiss, but kept for reference
export const getCountryPromptKey = (userId?: number) =>
  `country_prompt_done_${userId ?? "anon"}`;

const CountryPromptModal: React.FC<CountryPromptModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Just closes the modal without saving to localStorage for permanent dismiss
  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!selectedCountry) {
      setError("Please select a country to continue.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await dispatch(
        updateProfile({
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          country: selectedCountry,
        } as any),
      ).unwrap();
      dispatch(updateUserCountry(selectedCountry));
      handleClose();
    } catch (err: any) {
      setError(err?.message || "Failed to save country. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedInfo = COUNTRIES.find((c) => c.code === selectedCountry);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      showCloseButton={false}
      size="sm"
      closeOnBackdropClick={false}
    >
      <div className="py-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-900 to-indigo-900 border border-blue-700 flex items-center justify-center mx-auto mb-4">
            <GlobeAltIcon className="w-8 h-8 text-blue-300" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Where are you from?
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">
            Help us personalise your experience. This helps us provide relevant
            regional support and compliance information.
          </p>
        </div>

        {/* Country select */}
        <div className="mb-5">
          <label
            className={`${globalStyle.label} flex items-center gap-1.5 mb-2`}
          >
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            Your Country
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setError(null);
            }}
            className={`${globalStyle.input} ${error ? "border-red-500" : ""}`}
          >
            <option value="">— Select your country —</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.name}
              </option>
            ))}
          </select>
          {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
        </div>

        {/* Country preview */}
        {selectedInfo && (
          <div className="mb-5 flex items-center gap-3 p-3 rounded-lg bg-blue-900/20 border border-blue-800/40">
            <span className="text-3xl">{selectedInfo.flag}</span>
            <div>
              <p className="text-sm font-semibold text-blue-200">
                {selectedInfo.name}
              </p>
              <p className="text-xs text-blue-400">Selected country</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="tertiary"
            onClick={handleClose}
            fullWidth
            disabled={isLoading}
          >
            Skip
          </Button>
          <Button
            onClick={handleSave}
            fullWidth
            loading={isLoading}
            disabled={!selectedCountry || isLoading}
          >
            Save Country
          </Button>
        </div>

        <p className="text-center text-xs text-gray-600 mt-4">
          You can update this anytime in your profile settings.
        </p>
      </div>
    </Modal>
  );
};

export default CountryPromptModal;
